import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { apiFetch } from '@/lib/apiFetch'
import { useExerciseSession } from '@/hooks/useExerciseSession'
import { fetchMagnumMoment, EffectData } from '@/lib/magnumMoment'
import EffectRenderer from '@/components/EffectRenderer'
import MusicStaff from '@/components/MusicStaff'
import { useMusicPlayback } from '@/hooks/useMusicPlayback'
import {
  tierForLevel,
  generateInterval,
  generateChoices,
  type Interval,
  type GeneratedInterval,
  type ClefPreference,
} from '@/lib/musicTheory'

type GameState = 'idle' | 'loading' | 'playing' | 'result' | 'done'

const QUESTIONS_PER_SESSION = 10
const STREAK_MILESTONES = [3, 5, 7, 10]

interface Question {
  generated: GeneratedInterval
  choices: Interval[]
}

export default function IntervalRecognitionPage() {
  const { user } = useAuth()
  const magnum = useMagnum()
  const { sessionId, stats, activeMs, isIdle, startSession, endSession, recordAttempt, dismissIdle, persist } =
    useExerciseSession({ exerciseType: 'INTERVAL_RECOGNITION' })
  const { playInterval, isPlaying } = useMusicPlayback()

  const [state, setState] = useState<GameState>('idle')
  const [questions, setQuestions] = useState<Question[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [chosenAbbr, setChosenAbbr] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<{ correct: boolean } | null>(null)
  const [activeEffects, setActiveEffects] = useState<EffectData[] | null>(null)
  const [autoplay, setAutoplay] = useState(false)
  const questionStartRef = useRef(Date.now())
  const settingsRef = useRef<{ clefPreference: ClefPreference; musicDifficultyLevel: number }>({
    clefPreference: 'TREBLE',
    musicDifficultyLevel: 1.0,
  })

  const currentQuestion = questions[currentIndex] || null

  useEffect(() => {
    magnum.setFrameIdle(isIdle)
    if (isIdle) {
      fetchMagnumMoment({
        contextType: 'IDLE_RETURN',
        exerciseType: 'INTERVAL_RECOGNITION',
      category: 'MUSIC',
        streak: stats.streak,
        sessionId,
        activeMs,
      })
        .then((m) => magnum.handleEngineResponse(m))
        .catch(() => magnum.say("Still here. Still waiting. Still adorable."))
    }
  }, [isIdle])

  function generateQuestions(clef: ClefPreference, level: number): Question[] {
    const tier = tierForLevel(level)
    const qs: Question[] = []
    for (let i = 0; i < QUESTIONS_PER_SESSION; i++) {
      const intervalType = Math.random() < 0.5 ? 'melodic' as const : 'harmonic' as const
      const generated = generateInterval(tier, clef, intervalType)
      const choices = generateChoices(generated.interval, tier)
      qs.push({ generated, choices })
    }
    return qs
  }

  const startGame = async () => {
    setState('loading')
    try {
      const [settings, diffData] = await Promise.all([
        apiFetch('/music/settings/mine'),
        apiFetch('/game-difficulty/mine/INTERVAL_RECOGNITION'),
        startSession(),
      ])
      settingsRef.current = { ...settings, musicDifficultyLevel: diffData.difficultyLevel }
      const qs = generateQuestions(settings.clefPreference, diffData.difficultyLevel)
      setQuestions(qs)
      setCurrentIndex(0)
      setChosenAbbr(null)
      setFeedback(null)
      setState('playing')
      questionStartRef.current = Date.now()

      fetchMagnumMoment({
        contextType: 'GAME_STARTED',
        exerciseType: 'INTERVAL_RECOGNITION',
      category: 'MUSIC',
        streak: 0,
        sessionId,
      })
        .then((m) => magnum.handleEngineResponse(m, 'happy'))
        .catch(() => {})
    } catch {
      setState('idle')
    }
  }

  const handleChoice = async (choice: Interval) => {
    if (!currentQuestion || state !== 'playing') return

    const responseMs = Date.now() - questionStartRef.current
    const correct = choice.abbreviation === currentQuestion.generated.interval.abbreviation

    const newStreak = correct ? stats.streak + 1 : 0
    const isMilestone = correct && STREAK_MILESTONES.includes(newStreak)
    const contextType = correct
      ? isMilestone
        ? 'STREAK_MILESTONE'
        : 'CORRECT_ANSWER'
      : 'WRONG_ANSWER'

    setChosenAbbr(choice.abbreviation)
    setFeedback({ correct })
    setState('result')

    fetchMagnumMoment({
      contextType,
      exerciseType: 'INTERVAL_RECOGNITION',
      category: 'MUSIC',
      correct,
      responseMs,
      activeMs,
      streak: newStreak,
      sessionId,
    })
      .then((moment) => {
        magnum.handleEngineResponse(moment, correct ? 'happy' : 'encouraging')
        if (moment.effects?.length) setActiveEffects(moment.effects)
      })
      .catch(() => {})

    await recordAttempt(null, correct, responseMs, {
      intervalName: currentQuestion.generated.interval.abbreviation,
      playerAnswer: choice.abbreviation,
      rootNote: currentQuestion.generated.rootName,
      secondNote: currentQuestion.generated.secondName,
      clef: currentQuestion.generated.clef,
      intervalType: currentQuestion.generated.intervalType,
    })
  }

  const nextQuestion = () => {
    const next = currentIndex + 1
    if (next >= questions.length) {
      setState('done')

      fetchMagnumMoment({
        contextType: 'SESSION_END',
        exerciseType: 'INTERVAL_RECOGNITION',
      category: 'MUSIC',
        streak: stats.streak,
        sessionId,
        activeMs,
      })
        .then((m) => magnum.handleEngineResponse(m, 'excited'))
        .catch(() => {})

      endSession()
    } else {
      setCurrentIndex(next)
      setChosenAbbr(null)
      setFeedback(null)
      setState('playing')
      questionStartRef.current = Date.now()
    }
  }

  useEffect(() => {
    if (state === 'result') {
      const timer = setTimeout(nextQuestion, 2000)
      return () => clearTimeout(timer)
    }
  }, [state, currentIndex])

  useEffect(() => {
    if (autoplay && state === 'playing' && currentQuestion) {
      playInterval(currentQuestion.generated.abc)
    }
  }, [currentIndex, state, autoplay])

  useEffect(() => {
    startGame()
  }, [])

  useEffect(() => {
    if (isIdle && state === 'result') dismissIdle()
  }, [state])

  if (!user || user.type !== 'child') return null

  if (state === 'loading') {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0
    return (
      <div className="py-8 text-center space-y-8">
        <div className="text-6xl">🎶</div>
        <h1 className="text-3xl font-bold">Session Complete!</h1>
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{stats.correct}</p>
            <p className="text-xs text-muted-foreground">Correct</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{accuracy}%</p>
            <p className="text-xs text-muted-foreground">Accuracy</p>
          </div>
        </div>
        <div className="flex gap-4 justify-center pt-4">
          <button
            onClick={startGame}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
          >
            Play Again
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="text-center space-y-6">
      <EffectRenderer effects={activeEffects} onDone={() => setActiveEffects(null)} />

      <div className="flex items-center justify-between">
        <Link to="/practice" className="text-sm text-muted-foreground hover:text-foreground transition">
          &larr; Back
        </Link>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>
            {currentIndex + 1} of {questions.length}
          </span>
          <span className="text-primary font-medium">{stats.correct} correct</span>
          {stats.streak >= 3 && <span className="text-amber-500 font-medium">🔥 {stats.streak}</span>}
        </div>
      </div>

      <div className="w-full bg-accent rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{
            width: `${((currentIndex + (state === 'result' ? 1 : 0)) / questions.length) * 100}%`,
          }}
        />
      </div>

      {currentQuestion && (
        <>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              What interval is shown on the staff?
              {currentQuestion.generated.intervalType === 'harmonic' ? ' (played together)' : ' (played in sequence)'}
            </p>
            <div className="max-w-xs mx-auto bg-white rounded-xl p-4 border border-border">
              <MusicStaff abc={currentQuestion.generated.abc} width={180} />
            </div>
            <div className="flex items-center justify-center gap-4 mt-3">
              <button
                onClick={() => playInterval(currentQuestion.generated.abc)}
                disabled={isPlaying}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-accent transition disabled:opacity-50"
              >
                {isPlaying ? '🔊 Playing...' : '🔊 Hear It'}
              </button>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={autoplay}
                  onChange={(e) => setAutoplay(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                />
                Auto-play
              </label>
            </div>
          </div>

          <div className="space-y-3 max-w-md mx-auto">
            {currentQuestion.choices.map((choice) => {
              let btnClass = 'w-full px-6 py-4 rounded-xl text-lg font-bold transition border-2 '
              if (state === 'result') {
                if (choice.abbreviation === currentQuestion.generated.interval.abbreviation) {
                  btnClass += 'bg-green-100 border-green-500 text-green-800'
                } else if (choice.abbreviation === chosenAbbr) {
                  btnClass += 'bg-red-100 border-red-500 text-red-800'
                } else {
                  btnClass += 'border-border text-muted-foreground opacity-50'
                }
              } else {
                btnClass += 'border-border hover:border-primary hover:bg-accent'
              }

              return (
                <button
                  key={choice.abbreviation}
                  onClick={() => handleChoice(choice)}
                  disabled={state === 'result'}
                  className={btnClass}
                >
                  {choice.name} ({choice.abbreviation})
                </button>
              )
            })}
          </div>

          {state === 'result' && feedback && (
            <div className={`text-lg font-semibold ${feedback.correct ? 'text-primary' : 'text-amber-500'}`}>
              {feedback.correct ? '✓ Correct!' : `✗ It was: ${currentQuestion.generated.interval.name}`}
            </div>
          )}
        </>
      )}
    </div>
  )
}
